import React, { useState, useEffect } from 'react';
import { Search, MapPin, X, Briefcase, SlidersHorizontal } from 'lucide-react';
import { fetchJobs } from '../services/api';
import JobDetailSheet from '../components/JobDetailSheet';
import FilterDrawer from '../components/FilterDrawer';
import FilterSuggestion from '../components/FilterSuggestion';
import { useJobFilters } from '../hooks/useJobFilters';
import {
  JOB_TYPE_LABELS, BRANCHE_LABELS, labelOf, jobTitle,
} from '../constants/jobOptions';
import './SearchPage.css';

const normalizeJob = (job) => ({
  ...job,
  company: job.company || job.company_name,
  type: labelOf(JOB_TYPE_LABELS, job.job_type) || job.type,
  logo_url: job.logo_url || job.company_logo,
});

// Schnellauswahl über die Branchen des Backends — dieselben Slugs wie im
// Filter, damit Chip und Drawer denselben Filter setzen.
const BRANCHE_SLUGS = Object.keys(BRANCHE_LABELS);

export default function SearchPage() {
  const {
    filters, setFilters, resetFilters, defaults, params, activeCount, isMinor, noteIsMinor,
  } = useJobFilters();
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showFilter, setShowFilter] = useState(false);

  const hasSearched = !!submitted || activeCount > 0;

  // Ein Effekt für alles: Erstaufruf, Suchbegriff und Filter laufen über
  // denselben Request. params kommt fertig serialisiert aus useJobFilters.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setTotal(null);
    fetchJobs({ limit: 20, ...params, ...(submitted ? { search: submitted } : {}) })
      .then(({ data }) => {
        if (cancelled) return;
        const raw = Array.isArray(data) ? data : data?.jobs || data?.data || data?.results || [];
        setJobs(raw.map(normalizeJob));
        setTotal(data?.pagination?.total ?? raw.length);
        // Der Endpoint meldet mit, ob der Nutzer minderjaehrig ist
        noteIsMinor(data?.is_minor);
      })
      .catch(() => { if (!cancelled) setJobs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params, submitted, noteIsMinor]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmitted(query);
  };

  const toggleBranche = (slug) => setFilters(prev => ({
    ...prev,
    branchen: prev.branchen.includes(slug)
      ? prev.branchen.filter(b => b !== slug)
      : [...prev.branchen, slug],
  }));

  const clearSearch = () => {
    setQuery('');
    setSubmitted('');
    resetFilters();
  };

  return (
    <div className="search-page">
      {/* Header */}
      <div className="search-page__header">
        <div className="search-page__titlerow">
          <h1>Jobs entdecken</h1>
          <button
            className={`search-filter-btn${activeCount > 0 ? ' search-filter-btn--active' : ''}`}
            onClick={() => setShowFilter(true)}
          >
            <SlidersHorizontal size={16} />
            Filter
            {activeCount > 0 && <span className="filter-count-badge">{activeCount}</span>}
          </button>
        </div>

        <form className="search-bar" onSubmit={handleSearch}>
          <Search size={18} className="search-bar__icon" />
          <input
            type="search"
            placeholder="Job, Firma oder Stichwort..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="search-bar__input"
            autoComplete="off"
          />
          {query && (
            <button type="button" className="search-bar__clear" onClick={clearSearch}>
              <X size={16} />
            </button>
          )}
          <button type="submit" className="search-bar__btn">Suchen</button>
        </form>

        {/* Branchen-Chips */}
        <div className="search-categories">
          {BRANCHE_SLUGS.map(slug => (
            <button
              key={slug}
              className={`search-cat-chip${filters.branchen.includes(slug) ? ' search-cat-chip--active' : ''}`}
              onClick={() => toggleBranche(slug)}
            >
              {labelOf(BRANCHE_LABELS, slug)}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      <div className="search-results">
        <FilterSuggestion
          filters={filters}
          defaults={defaults}
          total={total}
          search={submitted}
          onChange={setFilters}
        />

        {loading && (
          <div className="search-skeleton">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="search-skel-row">
                <div className="skel search-skel-logo" />
                <div className="search-skel-lines">
                  <div className="skel search-skel-line" style={{ width: `${62 - i * 5}%` }} />
                  <div className="skel search-skel-line search-skel-line--sm" style={{ width: `${45 - i * 4}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && hasSearched && jobs.length === 0 && (
          <div className="search-empty">
            <div className="search-empty__icon">🔍</div>
            <h3>Keine Ergebnisse</h3>
            <p>Versuche andere Suchbegriffe oder Filter.</p>
            <button className="search-empty__reset" onClick={clearSearch}>Zurücksetzen</button>
          </div>
        )}

        {!loading && (
          <>
            {jobs.length > 0 && (
              <p className="search-results__hint">
                {hasSearched
                  ? `${jobs.length} Job${jobs.length !== 1 ? 's' : ''} gefunden`
                  : 'Alle verfügbaren Jobs'}
              </p>
            )}
            <div className="search-job-list">
              {jobs.map(job => (
                <button
                  key={job.id}
                  className="search-job-item"
                  onClick={() => setSelectedJob(job)}
                >
                  <div className="search-job-logo">
                    {job.logo_url
                      ? <img src={job.logo_url} alt={job.company} />
                      : <span>{(job.company || 'J').slice(0, 2).toUpperCase()}</span>
                    }
                  </div>
                  <div className="search-job-info">
                    <p className="search-job-title">{jobTitle(job.title)}</p>
                    <p className="search-job-company">{job.company}</p>
                    <div className="search-job-meta">
                      {job.location && (
                        <span><MapPin size={12} /> {job.location}</span>
                      )}
                      {job.type && (
                        <span><Briefcase size={12} /> {job.type}</span>
                      )}
                    </div>
                  </div>
                  <div className="search-job-right">
                    {(job.salary_display || job.salary) && (
                      <span className="search-job-salary">{job.salary_display || job.salary}</span>
                    )}
                    {job.isNew && <span className="search-job-new">Neu</span>}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {selectedJob && (
        <JobDetailSheet
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onApply={() => setSelectedJob(null)}
        />
      )}
      {showFilter && (
        <FilterDrawer
          onClose={() => setShowFilter(false)}
          onApply={setFilters}
          values={filters}
          defaults={defaults}
          isMinor={isMinor}
        />
      )}
    </div>
  );
}
